import { types as sdkTypes } from './sdkLoader';
import { formatMoney } from './currency';
import { getUserSubscriptionLevel, getUserFee, SUBSCRIPTION_LEVEL_3, isStartup } from './types';

import config from '../config';

const { Money } = sdkTypes;

const customerLevel1Fee = +process.env.REACT_APP_CUSTOMER_LEVEL_1_FEE;
const customerLevel2Fee = +process.env.REACT_APP_CUSTOMER_LEVEL_2_FEE;
const customerLevel3Fee = +process.env.REACT_APP_CUSTOMER_LEVEL_3_FEE;

const providerLevel1Fee = +process.env.REACT_APP_PROVIDER_LEVEL_1_FEE;
const providerLevel2Fee = +process.env.REACT_APP_PROVIDER_LEVEL_2_FEE;
const providerLevel3Fee = +process.env.REACT_APP_PROVIDER_LEVEL_3_FEE;
const providerLevel4Fee = +process.env.REACT_APP_PROVIDER_LEVEL_4_FEE;

const startupLevel1Fee = +process.env.REACT_APP_STARTUP_LEVEL_1_FEE;
const startupLevel2Fee = +process.env.REACT_APP_STARTUP_LEVEL_2_FEE;

const feeMaxValue = +process.env.REACT_APP_FEE_MAX_VALUE;

const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

export const getFeeData = (currentUser, currentTransaction, isProvider) => {
  const isStartupProvider = isStartup(currentUser);
  const offerData = currentTransaction?.attributes?.protectedData?.offersData?.[0];
  const offerPriceAmount = +offerData.price;

  if (!currentUser && !offerData) return null;
  // User subscription level
  const subscriptionLevel = getUserSubscriptionLevel(currentUser);
  // Additional fee from transaction Metadata
  const additionalFeeProvider = currentTransaction?.attributes?.metadata?.additionalFeeProvider;
  const additionalFeeCustomer = currentTransaction?.attributes?.metadata?.additionalFeeCustomer;
  const additionalFee = isProvider ? additionalFeeProvider : additionalFeeCustomer;
  const additionalFeePercent = additionalFee?.feePercent || 0;
  const additionalFeeFixed = additionalFee?.feeFixed || 0;
  // Custom fee from user Private data
  const customFee = getUserFee(currentUser);
  const customFeePercent = customFee?.feePercent || 0;
  const customFeeFixed = customFee?.feeFixed || 0;

  const providerFeeValues = [providerLevel1Fee, providerLevel2Fee, providerLevel3Fee, providerLevel4Fee];
  const customerFeeValues = [customerLevel1Fee, customerLevel2Fee, customerLevel3Fee];
  const startupFeeValues = [startupLevel1Fee, startupLevel2Fee];
  const feeValues = isStartupProvider && isProvider ? startupFeeValues : isProvider ? providerFeeValues : customerFeeValues;

  let feePercent = !!subscriptionLevel ? feeValues[subscriptionLevel - 1] : feeValues[0];
  const isFeeOverLimit = (offerPriceAmount * feePercent / 100 > feeMaxValue) && subscriptionLevel >= SUBSCRIPTION_LEVEL_3;
  const isCustomFeeOverLimit = customFeePercent && (offerPriceAmount * customFeePercent / 100 > feeMaxValue) && subscriptionLevel >= SUBSCRIPTION_LEVEL_3;

  let allAdditionalFee = {
    additionalFeeLabel: additionalFee?.label || null,
    additionalFeePercent: additionalFeePercent || null,
    additionalFeeFixed: additionalFeeFixed || null
  }

  if (customFee) {
    return {
      feePercent: !isCustomFeeOverLimit ? customFeePercent : null,
      feeFixed: !!customFeeFixed ? customFeeFixed : null,
      ...allAdditionalFee
    }
  } else if (isStartupProvider && isProvider) {
    return {
      feePercent: !isFeeOverLimit ? feePercent : null,
      feeFixed: isFeeOverLimit ? feeMaxValue : null,
      ...allAdditionalFee
    };
  } else {
    return {
      feePercent: !isFeeOverLimit ? feePercent : null,
      feeFixed: isFeeOverLimit ? feeMaxValue : null,
      ...allAdditionalFee
    };
  }
};

export const getFeeFormatted = (offerFeeData, currentTransaction, intl) => {

  const { feePercent, feeFixed } = offerFeeData;
  const offerPriceAmount = +currentTransaction?.attributes?.protectedData?.offersData?.[0]?.price;
  const offerPriceCurrency = currentTransaction?.attributes?.protectedData?.offersData?.[0]?.currency || config.currencySymbol ;

  const feeAmount = feePercent ? offerPriceAmount * feePercent / 100 : feeFixed;

  if(!feeAmount) return null

  // const currentOfferFee = new Money(feeAmount, config.currency);
  const currentOfferFee = feeAmount.toFixed(2);

  // return currentOfferFee && priceData(currentOfferFee, intl)?.formattedPrice;
  return currentOfferFee && `${currentOfferFee} ${offerPriceCurrency}`;
};

export const getAdditionalFeeFormatted = (currentTransaction, intl, isProvider) => {

  const offerPriceAmount = +currentTransaction?.attributes?.protectedData?.offersData?.[0]?.price;
  const offerPriceCurrency = currentTransaction?.attributes?.protectedData?.offersData?.[0]?.currency || config.currencySymbol;

  const additionalFeeProvider = currentTransaction?.attributes?.metadata?.additionalFeeProvider;
  const additionalFeeCustomer = currentTransaction?.attributes?.metadata?.additionalFeeCustomer;
  const additionalFee = isProvider ? additionalFeeProvider : additionalFeeCustomer;

  const additionalFeePercent = additionalFee?.feePercent || 0;
  const additionalFeeFixed = additionalFee?.feeFixed || 0;

  const feeAmount = additionalFeePercent ? offerPriceAmount * additionalFeePercent / 100 : additionalFeeFixed;

  // const currentOfferAdditionalFee = new Money(feeAmount, config.currency);
  const currentOfferAdditionalFee = feeAmount?.toFixed(2);

  if(feeAmount === 0) return null

  // return currentOfferAdditionalFee && priceData(currentOfferAdditionalFee, intl)?.formattedPrice;
  return currentOfferAdditionalFee && `${currentOfferAdditionalFee} ${offerPriceCurrency}`;
};

export const getTotalPriceFormatted = (offerFeeData, currentUser, currentTransaction, intl, isProvider) => {
  // User subscription level
  const subscriptionLevel = getUserSubscriptionLevel(currentUser);

  const { feePercent = 0, feeFixed, additionalFeePercent = 0, additionalFeeFixed = 0 } = offerFeeData;
  const offerPriceAmount = +currentTransaction?.attributes?.protectedData?.offersData?.[0]?.price;
  const offerPriceCurrency = currentTransaction?.attributes?.protectedData?.offersData?.[0]?.currency || config.currencySymbol;

  const feeAmount = offerPriceAmount * (feePercent + additionalFeePercent) / 100;

  let fee = feeAmount

  if (!!feePercent && !additionalFeePercent && !!additionalFeeFixed) {
    fee = feeAmount + additionalFeeFixed
  }
  else if ((feeFixed + additionalFeeFixed > feeMaxValue) && subscriptionLevel >= SUBSCRIPTION_LEVEL_3) {
    fee = feeMaxValue
  }
  else if (!!feeFixed && !!additionalFeePercent) {
    fee = feeFixed + feeAmount
  }
  else if (!!feeFixed) {
    fee = feeFixed + additionalFeeFixed
  }

  const totalAmount = isProvider ? offerPriceAmount - fee : offerPriceAmount + fee;

  // const currentOfferTotalPrice = new Money(totalAmount, config.currency);
  const currentOfferTotalPrice = totalAmount?.toFixed(2);

  // return currentOfferTotalPrice && priceData(currentOfferTotalPrice, intl)?.formattedPrice;
  return currentOfferTotalPrice && `${currentOfferTotalPrice} ${offerPriceCurrency}`;
};
